import React, { createContext, PropsWithChildren, useCallback, useMemo, useState } from "react";
import { CustomSnackbar, SnackbarProps } from "../../Form/CustomSnackbar";
import { v4 } from "uuid";

type State = {
    id: string;
    message: string;
    options: {
        onClose: () => void;
        severity: "error" | "success";
    } & Partial<{
        onClick: () => void;
        onRetry: () => void;
        autoHideDurationInSec: number;
    }>;
};

type SnackbarContextType = {
    close: () => void;
    state: State | undefined;
    update: (severity: "error" | "success", message: string, options?: Partial<SnackbarProps>) => void;
};

export const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

const SnackbarProvider: React.FC<PropsWithChildren> = props => {
    const [state, setState] = useState<State>();
    const [openId, setOpenId] = useState<string>();

    const update = useCallback((severity: "error" | "success", message: string, options?: Partial<SnackbarProps>) => {
        const id = v4();
        setState({
            id: id,
            message: message,
            options: {
                ...options,
                autoHideDurationInSec: options?.autoHideDurationInSec ?? (severity === "error" ? 5 : 3),
                severity: severity,
                onClose: () => {
                    setOpenId(cur => (cur === id ? undefined : cur));
                    options?.onClose && options.onClose();
                }
            }
        });
        setOpenId(id);
    }, []);

    const close = useCallback(() => {
        setOpenId(undefined);
    }, []);

    const snackbarPropsState: SnackbarContextType = useMemo(() => ({
        state,
        close,
        update
    }), [close, state, update]);

    return (
        <SnackbarContext.Provider value={snackbarPropsState}>
            {props.children}
            <CustomSnackbar
                open={!!openId}
                message={state?.message}
                severity={state?.options.severity}
                testId="Hooks-SnackbarProvider-CustomSnackbar"
                autoHideDurationInSec={state?.options.autoHideDurationInSec}
                onRetry={state?.options.onRetry}
                onClick={state?.options.onClick}
                onClose={state?.options.onClose} />
        </SnackbarContext.Provider>
    );
};

export default SnackbarProvider;
